import { useEffect, useState } from "react";
import { Star, Camera, X, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "../lib/api";

const Stars = ({ value, size = "w-4 h-4", onPick }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} data-testid={onPick ? `review-star-${n}` : undefined} onClick={onPick ? () => onPick(n) : undefined}
        className={`${size} ${n <= value ? "fill-[#D99B26] text-[#D99B26]" : "text-stone-300"} ${onPick ? "cursor-pointer hover:scale-110 transition-transform" : ""}`} />
    ))}
  </div>
);

const ReviewForm = ({ pid, onAdded }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const pick = (e) => setFiles((f) => [...f, ...Array.from(e.target.files)].slice(0, 3));
  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please choose a star rating");
    const fd = new FormData();
    fd.append("rating", rating); fd.append("comment", comment);
    files.forEach((f) => fd.append("photos", f));
    setBusy(true);
    try { await api.post(`/products/${pid}/reviews`, fd); toast.success("Thanks for your review!"); onAdded(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl p-5 border border-[#C85A32]/15 space-y-4" data-testid="review-form">
      <p className="font-semibold">Rate your purchase</p>
      <Stars value={rating} size="w-7 h-7" onPick={setRating} />
      <textarea data-testid="review-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={1000} rows={3} placeholder="How was the quality, fit and delivery?" className="w-full rounded-xl border p-3 text-sm" />
      <div className="flex flex-wrap gap-2 items-center">
        {files.map((f, i) => (
          <div key={i} className="relative">
            <img src={URL.createObjectURL(f)} alt="" className="w-16 h-16 rounded-lg object-cover" />
            <button type="button" onClick={() => setFiles(files.filter((_, k) => k !== i))} className="absolute -top-2 -right-2 bg-black text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
          </div>
        ))}
        {files.length < 3 && (
          <label className="w-16 h-16 rounded-lg border-2 border-dashed grid place-items-center cursor-pointer hover:border-[#C85A32] text-stone-400" data-testid="review-photo-label">
            <Camera className="w-5 h-5" />
            <input data-testid="review-photo-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={pick} className="hidden" />
          </label>
        )}
        <span className="text-xs text-stone-400">Up to 3 photos</span>
      </div>
      <button data-testid="review-submit-btn" disabled={busy} className="px-6 h-11 rounded-full bg-[#C85A32] text-white font-semibold disabled:opacity-50">{busy ? "Posting..." : "Post review"}</button>
    </form>
  );
};

export const Reviews = ({ pid }) => {
  const [data, setData] = useState(null);
  const load = () => api.get(`/products/${pid}/reviews`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [pid]); // eslint-disable-line
  if (!data) return null;
  return (
    <section className="mt-20" data-testid="reviews-section">
      <div className="flex items-end gap-4">
        <h2 className="font-display text-2xl font-extrabold">Customer reviews</h2>
        {data.count > 0 && <div className="flex items-center gap-2 text-sm text-stone-500" data-testid="reviews-average"><Stars value={Math.round(data.average)} /> {data.average} · {data.count} review{data.count > 1 ? "s" : ""}</div>}
      </div>
      <div className="grid lg:grid-cols-[1fr_380px] gap-8 mt-6">
        <div className="space-y-4">
          {data.count === 0 && <p className="text-stone-500 text-sm" data-testid="reviews-empty">No reviews yet. Customers who buy this item can leave the first review.</p>}
          {data.items.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl p-5 border border-[#C85A32]/10" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><span className="font-semibold">{r.name}</span>{r.verified && <span className="flex items-center gap-1 text-xs text-[#1E5E3A]"><BadgeCheck className="w-4 h-4" />Verified buyer</span>}</div>
                <span className="text-xs text-stone-400">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              <div className="mt-2"><Stars value={r.rating} /></div>
              {r.comment && <p className="text-sm text-stone-700 mt-3">{r.comment}</p>}
              {r.photos?.length > 0 && <div className="flex gap-2 mt-3">{r.photos.map((p) => <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noreferrer"><img src={`${API}/files/${p}`} alt="" className="w-20 h-20 rounded-lg object-cover hover:opacity-90" /></a>)}</div>}
            </div>
          ))}
        </div>
        <div>
          {data.can_review ? <ReviewForm pid={pid} onAdded={load} /> : (
            <p className="text-sm text-stone-500 bg-white rounded-2xl p-5 border border-[#C85A32]/10" data-testid="review-locked-note">
              {data.already_reviewed ? "Thanks, you've already reviewed this item." : "Only verified buyers can review. Purchase this item and your review option will appear here once payment is confirmed."}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};
